import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, Bell, Building2, TrendingUp, Shield, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvestmentFlow() {
  const navigate = useNavigate();
  const { unitId } = useParams();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notified, setNotified] = useState(false);

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const fetchUnit = async () => {
      if (!unitId) {
        setLoading(false);
        return;
      }
      
      try {
        const res = await fetch(`${API}/api/units/${unitId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnit(data.unit);
        }
      } catch (error) {
        console.error('Error fetching unit:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUnit();
    // Pre-fill email from user
    if (user.email) setNotifyEmail(user.email);
  }, [unitId, token, user.email]);

  const formatINR = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const handleNotify = () => {
    if (!notifyEmail) {
      toast.error('Please enter your email');
      return;
    }
    // In production, this would save to a waitlist
    setNotified(true);
    toast.success('We will notify you when investment is enabled!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <span className="text-sm text-gray-500">Investment Portal</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Coming Soon Badge */}
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-2 mb-8">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">Coming Soon</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-light text-white mb-6">
            Online Investment
          </h1>
          
          <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
            We're finalizing our payment infrastructure to ensure secure, seamless investments. 
            The full investment flow will be available shortly.
          </p>

          {/* Unit Info (if available) */}
          {unit && (
            <div className="bg-white/[0.02] border border-white/10 p-8 mb-12 max-w-xl mx-auto text-left">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg text-white font-medium">{unit.unitName || unit.name}</h3>
                  <p className="text-sm text-gray-500">{unit.projectName}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Unit Value</p>
                  <p className="text-lg text-white font-mono">{formatINR(unit.financial?.unitValue || unit.value)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Min Investment</p>
                  <p className="text-lg text-white font-mono">{formatINR(unit.minInvestment || 500000)}</p>
                </div>
              </div>
            </div>
          )}

          {/* What's Coming */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/[0.02] border border-white/10 p-6">
              <div className="w-10 h-10 bg-emerald-500/20 flex items-center justify-center mb-4 mx-auto">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="text-white font-medium mb-2">Secure Payments</h3>
              <p className="text-sm text-gray-500">RBI-compliant payment processing with escrow protection</p>
            </div>
            
            <div className="bg-white/[0.02] border border-white/10 p-6">
              <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center mb-4 mx-auto">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <h3 className="text-white font-medium mb-2">Digital Documentation</h3>
              <p className="text-sm text-gray-500">E-sign LLP agreements instantly with legal compliance</p>
            </div>
            
            <div className="bg-white/[0.02] border border-white/10 p-6">
              <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center mb-4 mx-auto">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-white font-medium mb-2">Portfolio Tracking</h3>
              <p className="text-sm text-gray-500">Real-time dashboard to track your investments</p>
            </div>
          </div>

          {/* Notify Me Form */}
          <div className="bg-white/[0.02] border border-white/10 p-8 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg text-white">Get Notified</h3>
            </div>
            
            {notified ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-500/20 flex items-center justify-center mx-auto mb-4 rounded-full">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-emerald-400 mb-2">You are on the list!</p>
                <p className="text-sm text-gray-500">We will email you at {notifyEmail} when investments go live.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-400 mb-4">
                  Enter your email and we will notify you when online investment is enabled.
                </p>
                
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50"
                  />
                  <Button
                    onClick={handleNotify}
                    className="bg-emerald-500 hover:bg-emerald-600 text-black px-6"
                  >
                    Notify Me
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Contact Alternative */}
          <p className="text-sm text-gray-500 mt-8">
            Want to invest now? Contact your broker or reach out to{' '}
            <a href="mailto:invest@altslice.com" className="text-emerald-400 hover:underline">
              invest@altslice.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
